import { GameObject } from '../base/GameObject';
import { Monster } from '../monsters/Monster';
import { getEquipmentManager } from '../base/UserProfile';
import { EquipmentStats } from '../../data/EquipmentData';
import { AudioManager, SoundEffect } from '../../systems/AudioManager';

export abstract class Tower extends GameObject {
  protected baseDamage: number;
  protected baseAttackSpeed: number; // attacks per second
  protected baseRange: number;
  protected lastAttackTime: number = 0;
  protected level: number = 1;
  protected upgradeCost: number = 50;
  protected maxLevel: number = 10;
  protected targetingRange: number;
  protected audioManager: AudioManager;

  // Equipment-enhanced stats
  protected effectiveDamage: number;
  protected effectiveAttackSpeed: number;
  protected effectiveRange: number;
  protected critChance: number = 0;
  protected critDamage: number = 2.0;
  protected projectileCount: number = 1;
  protected pierceCount: number = 0;

  constructor(scene: Phaser.Scene, x: number, y: number, damage: number, attackSpeed: number, range: number) {
    super(scene, x, y);
    this.baseDamage = damage;
    this.baseAttackSpeed = attackSpeed * 2.0; // 2x faster attack speed
    this.baseRange = range;
    this.targetingRange = range;
    
    // Initialize audio manager
    this.audioManager = AudioManager.getInstance(scene);
    
    // Calculate initial effective stats
    this.recalculateStats();
    
    // Create basic visuals first
    this.createVisuals();
    
    // Initialize enhanced visual effects
    this.initializeVisualEffects();
  }

  private initializeVisualEffects(): void {
    // Enhanced visual effects for satellites
    this.createOrbitalTrail();
    this.createStatusLights();
    this.createEnergyFieldPulse();
  }

  private createOrbitalTrail(): void {
    // Create subtle orbital trail effect
    const trail = this.scene.add.graphics();
    trail.lineStyle(1, 0x74b9ff, 0.2);
    trail.strokeCircle(0, 0, 40);
    trail.setPosition(this.x, this.y);
    trail.setDepth(this.depth - 1);
    
    // Gentle fade animation
    this.scene.tweens.add({
      targets: trail,
      alpha: 0.05,
      duration: 3000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  private createStatusLights(): void {
    // Create status indicator lights
    for (let i = 0; i < 3; i++) {
      const angle = (Math.PI * 2 * i) / 3;
      const x = Math.cos(angle) * 8;
      const y = Math.sin(angle) * 8;
      
      const light = this.scene.add.circle(x, y, 1, 0x00ff00, 0.8);
      this.add(light);
      
      // Blinking animation
      this.scene.tweens.add({
        targets: light,
        alpha: 0.2,
        duration: 800 + (i * 200),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    }
  }

  private createEnergyFieldPulse(): void {
    // Create energy field pulse effect
    this.scene.time.addEvent({
      delay: 5000,
      callback: () => {
        const pulse = this.scene.add.graphics();
        pulse.lineStyle(2, 0x3498db, 0.6);
        pulse.strokeCircle(this.x, this.y, 20);
        pulse.setDepth(this.depth + 1);
        
        this.scene.tweens.add({
          targets: pulse,
          scaleX: 3,
          scaleY: 3,
          alpha: 0,
          duration: 1500,
          ease: 'Power2.easeOut',
          onComplete: () => pulse.destroy()
        });
      },
      loop: true
    });
  }

  protected createVisuals(): void {
    // Clear any existing visuals
    this.removeAll();
    
    // Create satellite base design
    this.createSatelliteStructure();
    
    // Create solar panels
    this.createSolarPanels();
    
    // Create communication array
    this.createCommunicationArray();
    
    // Create thrusters/positioning jets
    this.createThrusters();
    
    // Create energy core
    this.createEnergyCore();
    
    // Create orbital mechanics
    this.createOrbitalMechanics();
  }

  protected createSatelliteStructure(): void {
    // Main satellite body (hexagonal)
    const mainBody = this.scene.add.graphics();
    mainBody.fillStyle(0x2c3e50, 1);
    mainBody.lineStyle(2, 0x34495e, 1);
    
    // Hexagonal main structure
    const hexPoints = [];
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI * 2 * i) / 6;
      hexPoints.push(Math.cos(angle) * 12);
      hexPoints.push(Math.sin(angle) * 12);
    }
    mainBody.fillPoints(hexPoints, true);
    mainBody.strokePoints(hexPoints, true);
    
    // Central hub
    mainBody.fillStyle(0x34495e, 1);
    mainBody.fillCircle(0, 0, 8);
    
    // Corner reinforcements
    mainBody.fillStyle(0x7f8c8d, 1);
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI * 2 * i) / 6;
      const x = Math.cos(angle) * 10;
      const y = Math.sin(angle) * 10;
      mainBody.fillCircle(x, y, 2);
    }
    
    this.add(mainBody);
  }

  protected createSolarPanels(): void {
    // Create 4 solar panel arrays
    for (let i = 0; i < 4; i++) {
      const angle = (Math.PI * 2 * i) / 4;
      const distance = 18;
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance;
      
      const panel = this.scene.add.graphics();
      
      // Solar panel base
      panel.fillStyle(0x1a252f, 1);
      panel.lineStyle(1, 0x2c3e50, 1);
      panel.fillRect(-8, -6, 16, 12);
      panel.strokeRect(-8, -6, 16, 12);
      
      // Solar cells grid
      panel.lineStyle(0.5, 0x3498db, 0.8);
      for (let row = 0; row < 3; row++) {
        for (let col = 0; col < 4; col++) {
          const cellX = -6 + (col * 3);
          const cellY = -4 + (row * 2.5);
          panel.strokeRect(cellX, cellY, 2.5, 2);
        }
      }
      
      // Panel glow effect
      panel.lineStyle(1, 0x74b9ff, 0.6);
      panel.strokeRect(-8, -6, 16, 12);
      
      panel.setPosition(x, y);
      panel.setRotation(angle);
      this.add(panel);
      
      // Faster panel tracking oscillation
      this.scene.tweens.add({
        targets: panel,
        rotation: angle + 0.2,
        duration: 1500 + (i * 250), // Faster tracking (was 3000 + i*500)
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    }
  }

  protected createCommunicationArray(): void {
    // Main communication dish
    const dish = this.scene.add.graphics();
    dish.fillStyle(0x95a5a6, 1);
    dish.lineStyle(1, 0xbdc3c7, 1);
    dish.fillEllipse(0, -25, 16, 8);
    dish.strokeEllipse(0, -25, 16, 8);
    
    // Dish support arm
    dish.lineStyle(2, 0x7f8c8d, 1);
    dish.lineBetween(0, -21, 0, -10);
    
    // Focal point
    dish.fillStyle(0xe74c3c, 1);
    dish.fillCircle(0, -25, 1.5);
    
    this.add(dish);
    
    // Satellite tracking rotation
    this.scene.tweens.add({
      targets: dish,
      rotation: Math.PI * 2,
      duration: 20000,
      repeat: -1,
      ease: 'Linear'
    });

    // Secondary antennas
    for (let i = 0; i < 3; i++) {
      const antenna = this.scene.add.graphics();
      const angle = (Math.PI * 2 * i) / 3;
      const x = Math.cos(angle) * 15;
      const y = Math.sin(angle) * 15;
      
      antenna.lineStyle(1, 0xf39c12, 1);
      antenna.lineBetween(0, 0, 0, -8);
      antenna.fillStyle(0xe67e22, 1);
      antenna.fillCircle(0, -8, 1);
      
      antenna.setPosition(x, y);
      this.add(antenna);
    }
  }

  protected createThrusters(): void {
    // Positioning thrusters
    const thrusterPositions = [
      { x: 0, y: 20, angle: Math.PI },
      { x: 17, y: 10, angle: Math.PI * 1.3 },
      { x: -17, y: 10, angle: Math.PI * 0.7 }
    ];
    
    thrusterPositions.forEach(pos => {
      const thruster = this.scene.add.graphics();
      
      // Thruster nozzle
      thruster.fillStyle(0x34495e, 1);
      thruster.fillRect(-2, 0, 4, 6);
      
      // Thruster flame (intermittent)
      const flame = this.scene.add.graphics();
      flame.fillStyle(0xe74c3c, 0.8);
      flame.fillTriangle(0, 6, -3, 12, 3, 12);
      flame.fillStyle(0xf39c12, 0.6);
      flame.fillTriangle(0, 6, -1.5, 10, 1.5, 10);
      
      // thruster.add(flame);
      thruster.setPosition(pos.x, pos.y);
      thruster.setRotation(pos.angle);
      this.add(thruster);
      
      // Faster thruster firing animation
      this.scene.tweens.add({
        targets: flame,
        scaleY: 0.3,
        alpha: 0.3,
        duration: 100, // Much faster firing (was 200ms)
        yoyo: true,
        repeat: -1,
        delay: Math.random() * 1000 // More frequent firing
      });
    });
  }

  protected createEnergyCore(): void {
    // Energy core in center
    const core = this.scene.add.graphics();
    core.fillStyle(0x3498db, 0.8);
    core.fillCircle(0, 0, 4);
    
    // Core pulsing
    core.lineStyle(1, 0x74b9ff, 1);
    core.strokeCircle(0, 0, 4);
    
    this.add(core);
    
    // Energy pulse animation
    this.scene.tweens.add({
      targets: core,
      scaleX: 1.3,
      scaleY: 1.3,
      alpha: 0.5,
      duration: 1000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    
    // Energy discharge effect
    this.scene.time.addEvent({
      delay: 3000,
      callback: () => this.createEnergyDischarge(),
      loop: true
    });
  }

  protected createEnergyDischarge(): void {
    const discharge = this.scene.add.graphics();
    discharge.lineStyle(2, 0x74b9ff, 0.8);
    
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI * 2 * i) / 6;
      const length = 15 + Math.random() * 10;
      discharge.lineBetween(
        0, 0,
        Math.cos(angle) * length,
        Math.sin(angle) * length
      );
    }
    
    this.add(discharge);
    
    this.scene.tweens.add({
      targets: discharge,
      alpha: 0,
      scaleX: 2,
      scaleY: 2,
      duration: 400, // Faster discharge animation (was 800ms)
      ease: 'Power2.easeOut',
      onComplete: () => discharge.destroy()
    });
  }

  protected createOrbitalMechanics(): void {
    // Create orbital trail effect
    const orbitTrail = this.scene.add.graphics();
    orbitTrail.lineStyle(1, 0x74b9ff, 0.3);
    orbitTrail.strokeCircle(0, 0, 35);
    orbitTrail.setDepth(this.depth - 1);
    
    // Add to scene directly (not to tower) for proper positioning
    this.scene.add.existing(orbitTrail);
    orbitTrail.setPosition(this.x, this.y);
    
    // Faster orbital drift
    this.scene.tweens.add({
      targets: this,
      rotation: this.rotation + 0.1,
      duration: 4000, // Faster rotation (was 8000ms)
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  // Recalculate stats based on base stats + equipment bonuses
  protected recalculateStats(): void {
    const equipmentStats = this.getEquipmentStats();
    
    this.effectiveDamage = this.baseDamage + (equipmentStats.damage || 0);
    this.effectiveAttackSpeed = (this.baseAttackSpeed + (equipmentStats.attackSpeed || 0)) * 1.5; // Additional speed boost
    this.effectiveRange = this.baseRange + (equipmentStats.range || 0);
    this.targetingRange = this.effectiveRange;
    
    this.critChance = equipmentStats.critChance || 0;
    this.critDamage = 2.0 + (equipmentStats.critDamage || 0);
    this.projectileCount = 1 + (equipmentStats.projectileCount || 0);
    this.pierceCount = equipmentStats.pierceCount || 0;
  }

  // Get total equipment stats from equipped items
  protected getEquipmentStats(): EquipmentStats {
    const equipmentManager = getEquipmentManager();
    return equipmentManager.calculateTotalStats();
  }

  override update(time: number, delta: number): void {
    // Apply fast update multiplier
    const fastDelta = delta * 1.5; // 1.5x faster update cycles
    
    // Recalculate stats periodically (in case equipment changed)
    if (time % 1000 < fastDelta) { // Every ~1 second
      this.recalculateStats();
    }
    
    // Find and attack targets more frequently
    const target = this.findTarget();
    if (target && this.canAttack(time)) {
      this.attack(target, time);
    }
  }

  private canAttack(currentTime: number): boolean {
    const attackInterval = 1000 / this.effectiveAttackSpeed; // Convert to milliseconds
    const fastAttackInterval = attackInterval * 0.7; // 30% faster attacks
    return currentTime - this.lastAttackTime >= fastAttackInterval;
  }

  protected attack(target: Monster, currentTime: number): void {
    this.lastAttackTime = currentTime;
    
    // Calculate damage with equipment bonuses
    let damage = this.effectiveDamage;
    
    // Crit chance calculation
    const isCrit = Math.random() < this.critChance;
    if (isCrit) {
      damage *= this.critDamage;
    }
    
    // Fire multiple projectiles if equipped
    for (let i = 0; i < this.projectileCount; i++) {
      this.fireProjectile(target, damage, isCrit);
    }
    
    // Enhanced muzzle flash with faster animation
    this.createFastMuzzleFlash();
    
    // Play attack sound
    this.audioManager.playSFX(SoundEffect.TOWER_SHOOT);
  }

  protected findTarget(): Monster | null {
    if (!this.scene) return null;
    var _this = this;
    // Get monsters from scene data (set by GamePlayManager)
    const sceneData = (this.scene as any);
    const monsters: Monster[] = sceneData.monsters || [];
    
    if (monsters.length === 0) return null;
    
    let closestMonster: Monster | null = null;
    let closestDistance = Infinity;
    
    monsters.forEach((monster: Monster) => {
      if (monster && monster?.getIsActive && monster?.getIsActive() && monster.getHealth() > 0) {
        const distance = Phaser.Math.Distance.Between(_this.x, _this.y, monster.x, monster.y);
        if (distance <= _this.targetingRange && distance < closestDistance) {
          closestDistance = distance;
          closestMonster = monster;
        }
      }
    });
    
    // Debug logging (remove later)
    // if (closestMonster) {
    //   console.log(`Tower found target at distance ${closestDistance.toFixed(2)}, range: ${this.targetingRange}`);
    // }
    
    return closestMonster;
  }

  protected fireProjectile(target: Monster, damage: number, isCrit: boolean): void {
    // Create muzzle flash effect
    this.createMuzzleFlash();
    
    // Create enhanced projectile with trail
    const projectile = this.createEnhancedProjectile(isCrit);
    
    // Animate projectile with trail effect
    this.animateProjectileToTarget(projectile, target, damage, isCrit);
  }

  protected createMuzzleFlash(): void {
    const flash = this.scene.add.circle(this.x, this.y - 15, 8, 0xffffff, 0.8);
    
    this.scene.tweens.add({
      targets: flash,
      scaleX: 1.5,
      scaleY: 1.5,
      alpha: 0,
      duration: 100,
      ease: 'Power2',
      onComplete: () => flash.destroy()
    });
  }

  private createFastMuzzleFlash(): void {
    const muzzleFlash = this.scene.add.circle(this.x, this.y - 15, 8, 0xffffff, 0.9);
    muzzleFlash.setDepth(this.depth + 1);
    
    this.scene.tweens.add({
      targets: muzzleFlash,
      scaleX: 2,
      scaleY: 2,
      alpha: 0,
      duration: 80, // Much faster flash (was 150ms)
      ease: 'Power2.easeOut',
      onComplete: () => muzzleFlash.destroy()
    });
  }

  protected createEnhancedProjectile(isCrit: boolean): Phaser.GameObjects.Container {
    const projectileContainer = this.scene.add.container(this.x, this.y);
    
    // Main projectile
    const projectileCore = this.scene.add.circle(0, 0, isCrit ? 4 : 3, isCrit ? 0xff4444 : 0xffaa00);
    projectileContainer.add(projectileCore);
    
    // Glow effect
    const glow = this.scene.add.circle(0, 0, isCrit ? 8 : 6, isCrit ? 0xff0000 : 0xffff00, 0.3);
    projectileContainer.add(glow);
    
    // Crit spark effect
    if (isCrit) {
      for (let i = 0; i < 4; i++) {
        const spark = this.scene.add.rectangle(0, 0, 2, 8, 0xffffff);
        spark.setRotation((Math.PI * 2 * i) / 4);
        projectileContainer.add(spark);
      }
    }
    
    // Pulsing animation
    this.scene.tweens.add({
      targets: glow,
      scaleX: 1.3,
      scaleY: 1.3,
      duration: 200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    
    return projectileContainer;
  }

  protected animateProjectileToTarget(projectile: Phaser.GameObjects.Container, target: Monster, damage: number, isCrit: boolean): void {
    // Calculate travel time based on distance
    const distance = Phaser.Math.Distance.Between(this.x, this.y, target.x, target.y);
    const speed = 300; // pixels per second
    const travelTime = (distance / speed) * 1000;
    
    // Create trail effect
    this.createProjectileTrail(projectile, travelTime);
    
    // Animate projectile to target
    this.scene.tweens.add({
      targets: projectile,
      x: target.x,
      y: target.y,
      duration: travelTime,
      ease: 'Power2',
      onComplete: () => {
        // Create impact effect
        this.createImpactEffect(target.x, target.y, isCrit);
        
        // Apply damage
        if (this.pierceCount > 0) {
          this.applyPiercingDamage(target, damage, this.pierceCount);
        } else {
          target.takeDamage(damage);
        }
        
        // Show crit effect
        if (isCrit) {
          this.showEnhancedCritEffect(target.x, target.y);
        }
        
        projectile.destroy();
      }
    });
  }

  protected createProjectileTrail(projectile: Phaser.GameObjects.Container, duration: number): void {
    const trailParticles: Phaser.GameObjects.Arc[] = [];
    
    // Create trail particles periodically
    const trailTimer = this.scene.time.addEvent({
      delay: 20,
      repeat: Math.floor(duration / 20),
      callback: () => {
        const particle = this.scene.add.circle(projectile.x, projectile.y, 2, 0xffaa00, 0.6);
        trailParticles.push(particle);
        
        // Fade out trail particle
        this.scene.tweens.add({
          targets: particle,
          alpha: 0,
          scaleX: 0,
          scaleY: 0,
          duration: 300,
          ease: 'Power2',
          onComplete: () => particle.destroy()
        });
      }
    });
    
    // Cleanup timer when projectile is destroyed
    this.scene.time.delayedCall(duration + 100, () => {
      trailTimer.destroy();
    });
  }

  protected createImpactEffect(x: number, y: number, isCrit: boolean): void {
    // Impact flash
    const impactFlash = this.scene.add.circle(x, y, isCrit ? 15 : 10, 0xffffff, 0.9);
    this.scene.tweens.add({
      targets: impactFlash,
      scaleX: 2,
      scaleY: 2,
      alpha: 0,
      duration: 200,
      ease: 'Power2',
      onComplete: () => impactFlash.destroy()
    });
    
    // Impact particles
    for (let i = 0; i < (isCrit ? 8 : 5); i++) {
      const angle = (Math.PI * 2 * i) / (isCrit ? 8 : 5);
      const particle = this.scene.add.circle(x, y, 2, isCrit ? 0xff4444 : 0xffaa00);
      
      const targetX = x + Math.cos(angle) * (isCrit ? 25 : 15);
      const targetY = y + Math.sin(angle) * (isCrit ? 25 : 15);
      
      this.scene.tweens.add({
        targets: particle,
        x: targetX,
        y: targetY,
        alpha: 0,
        duration: 400,
        ease: 'Power2',
        onComplete: () => particle.destroy()
      });
    }
  }

  protected showEnhancedCritEffect(x: number, y: number): void {
    // Enhanced crit text with outline
    const critText = this.scene.add.text(x, y, 'CRITICAL!', {
      fontSize: '18px',
      color: '#ff0000',
      fontFamily: 'Arial',
      fontStyle: 'bold',
      stroke: '#ffffff',
      strokeThickness: 2
    }).setOrigin(0.5);
    
    // Crit explosion effect
    const critExplosion = this.scene.add.circle(x, y, 5, 0xff0000, 0.8);
    
    // Animate text
    this.scene.tweens.add({
      targets: critText,
      y: y - 40,
      scaleX: 1.3,
      scaleY: 1.3,
      alpha: 0,
      duration: 1200,
      ease: 'Power2',
      onComplete: () => critText.destroy()
    });
    
    // Animate explosion
    this.scene.tweens.add({
      targets: critExplosion,
      scaleX: 3,
      scaleY: 3,
      alpha: 0,
      duration: 500,
      ease: 'Power2',
      onComplete: () => critExplosion.destroy()
    });
    
    // Screen shake for crit
    this.scene.cameras.main.shake(100, 0.01);
  }

  // Keep original showCritEffect for backward compatibility
  protected showCritEffect(x: number, y: number): void {
    this.showEnhancedCritEffect(x, y);
  }

  protected applyPiercingDamage(initialTarget: Monster, damage: number, remainingPierces: number): void {
    // Apply damage to initial target
    initialTarget.takeDamage(damage);
    
    if (remainingPierces <= 0) return;
    
    // Find next target for piercing from scene monsters
    const sceneData = (this.scene as any);
    const monsters: Monster[] = sceneData.monsters || [];
    
    let nextTarget: Monster | null = null;
    let closestDistance = Infinity;
    
    monsters.forEach((monster) => {
      if (monster && monster !== initialTarget && monster?.getIsActive && monster?.getIsActive() && monster.getHealth() > 0) {
        const distance = Phaser.Math.Distance.Between(initialTarget.x, initialTarget.y, monster.x, monster.y);
        if (distance <= 100 && distance < closestDistance) { // Pierce range: 100px
          closestDistance = distance;
          nextTarget = monster;
        }
      }
    });
    
    if (nextTarget) {
      // Recursive piercing
      this.applyPiercingDamage(nextTarget, damage * 0.8, remainingPierces - 1); // 20% damage reduction per pierce
    }
  }

  // Upgrade tower
  upgrade(): boolean {
    if (this.level >= this.maxLevel) return false;
    
    this.level++;
    this.baseDamage += 10;
    this.baseAttackSpeed += 0.1;
    this.baseRange += 5;
    this.upgradeCost = Math.floor(this.upgradeCost * 1.5);
    
    // Play upgrade sound
    this.audioManager.playSFX('tower_upgrade' as any);
    
    // Recalculate with new base stats
    this.recalculateStats();
    
    return true;
  }

  // Getters
  getLevel(): number {
    return this.level;
  }

  getUpgradeCost(): number {
    return this.upgradeCost;
  }

  canUpgrade(): boolean {
    return this.level < this.maxLevel;
  }

  getDamage(): number {
    return this.effectiveDamage;
  }

  getRange(): number {
    return this.effectiveRange;
  }

  getAttackSpeed(): number {
    return this.effectiveAttackSpeed;
  }

  // Get stats display for UI
  getStatsDisplay(): string {
    return `Dmg: ${this.effectiveDamage.toFixed(0)} | Speed: ${this.effectiveAttackSpeed.toFixed(1)} | Range: ${this.effectiveRange.toFixed(0)}` +
           (this.critChance > 0 ? ` | Crit: ${(this.critChance * 100).toFixed(1)}%` : '') +
           (this.projectileCount > 1 ? ` | Projectiles: ${this.projectileCount}` : '') +
           (this.pierceCount > 0 ? ` | Pierce: ${this.pierceCount}` : '');
  }
} 